import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icon';
import { useT } from '../i18n/useT';

/**
 * The shell every dialog in the app renders into.
 *
 * Each dialog used to hand-roll the overlay markup, which meant none of them
 * handled Escape, none trapped Tab (so focus wandered into the editor behind
 * the overlay) and none restored focus to whatever opened them. Centralising
 * that here fixes all of it in one place.
 */

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  onClose: () => void;
  /** Visible heading. Also used as the dialog's accessible name. */
  title: ReactNode;
  /** Accessible name when `title` isn't plain text. */
  label?: string;
  /** Extra classes on the dialog box (e.g. `mb-modal-wide`). */
  className?: string;
  /** Hide the header row — for dialogs that draw their own. */
  bare?: boolean;
  children: ReactNode;
}

export function Modal({ onClose, title, label, className, bare, children }: ModalProps) {
  const t = useT();
  const boxRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement) : null,
  );

  const close = useCallback(() => {
    returnFocusTo.current?.focus?.();
    onClose();
  }, [onClose]);

  // Move focus into the dialog on open. The close button leads in DOM order but
  // is a dead end, so skip it and land on the first real control; fall back to
  // the box itself for a dialog that has nothing focusable.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const items = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = items.find((el) => !el.classList.contains('mb-modal-close'));
    (first ?? items[0] ?? box).focus();
  }, []);

  // Escape closes; Tab cycles within the dialog instead of escaping behind it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // A menu opened from inside the dialog owns Escape first: it listens on
        // the bubble phase, which this capture-phase handler would preempt.
        if (document.querySelector('[role="menu"]')) return;
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      const box = boxRef.current;
      if (!box) return;
      const items = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === box)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [close]);

  return (
    <div className="mb-modal-overlay" onClick={close}>
      <div
        ref={boxRef}
        className={className ? `mb-modal ${className}` : 'mb-modal'}
        role="dialog"
        aria-modal="true"
        aria-label={label ?? (typeof title === 'string' ? title : undefined)}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {!bare && (
          <div className="mb-modal-head">
            <h2 className="mb-modal-title">{title}</h2>
            <button
              className="mb-modal-close"
              onClick={close}
              title={t('common.close')}
              aria-label={t('common.close')}
            >
              <Icon name="x" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
